import React from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import {
  Check as CheckIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import { managerComponentStyles } from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/components/styles.ts';
import type { TApiReadyCovenant } from '~/modules/covenants/types.ts';
import { StatusChipWithTooltip } from '~/modules/covenants/components/StatusChipWithTooltip.tsx';
import {
  formatReadyDocumentPeriod,
  getReadyCovenantCopyTooltipTitle,
  getReadyCovenantStatusConfig,
  getReadyCovenantTargetStatusConfig,
} from '../../utils/utils.ts';
import {
  MANAGER_READY_COVENANT_CONTRACT_LABEL,
  MANAGER_READY_COVENANT_COPY_ARIA_LABEL,
  MANAGER_READY_COVENANT_DEADLINE_LABEL,
  MANAGER_READY_COVENANT_MONITORING_ID_LABEL,
  MANAGER_READY_COVENANT_PERIOD_LABEL,
  MANAGER_READY_COVENANT_STATUS_LABEL,
  MANAGER_READY_COVENANT_TARGET_STATUS_LABEL,
  MANAGER_READY_COVENANTS_EMPTY_VALUE,
} from '../../constants.ts';

interface IManagerReadyCovenantMetaInfoProps {
  covenant: TApiReadyCovenant;
  covenantKey: string;
  copiedCovenantKey: string | null;
  onCopyMonitoringId: (covenantKey: string, grafId: number) => void | Promise<void>;
}

export const ReadyCovenantMetaInfo: React.FC<IManagerReadyCovenantMetaInfoProps> = ({
  covenant,
  covenantKey,
  copiedCovenantKey,
  onCopyMonitoringId,
}) => {
  const covenantStatusConfig = getReadyCovenantStatusConfig(covenant.status);
  const covenantTargetStatusConfig = getReadyCovenantTargetStatusConfig(covenant.targetStatus);
  const targetStatusChipSx =
    covenantTargetStatusConfig.variant === 'outlinedSuccess'
      ? managerComponentStyles.readyTargetStatus('success')
      : covenantTargetStatusConfig.variant === 'outlinedError'
        ? managerComponentStyles.readyTargetStatus('error')
        : undefined;

  return (
    <Box sx={managerComponentStyles.readyPreviewGrid}>
      <Box sx={managerComponentStyles.readyPreviewItem}>
        <Typography variant="caption" sx={managerComponentStyles.readyPreviewLabel}>
          {MANAGER_READY_COVENANT_MONITORING_ID_LABEL}
        </Typography>
        <Box sx={managerComponentStyles.readyPreviewMonitoringRow}>
          <Typography variant="body2" sx={managerComponentStyles.readyPreviewValue}>
            {covenant.grafId}
          </Typography>
          <Tooltip
            title={getReadyCovenantCopyTooltipTitle(copiedCovenantKey, covenantKey)}
            placement="top"
          >
            <IconButton
              size="small"
              color="primary"
              onClick={() => {
                void onCopyMonitoringId(covenantKey, covenant.grafId);
              }}
              aria-label={MANAGER_READY_COVENANT_COPY_ARIA_LABEL}
              sx={managerComponentStyles.readyCopyBtn}
            >
              {copiedCovenantKey === covenantKey ? (
                <CheckIcon fontSize="small" color="success" />
              ) : (
                <ContentCopyIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <Box sx={managerComponentStyles.readyPreviewItem}>
        <Typography variant="caption" sx={managerComponentStyles.readyPreviewLabel}>
          {MANAGER_READY_COVENANT_CONTRACT_LABEL}
        </Typography>
        <Typography variant="body2" sx={managerComponentStyles.readyPreviewValue}>
          {covenant.dealNumber || MANAGER_READY_COVENANTS_EMPTY_VALUE}
        </Typography>
      </Box>
      <Box sx={managerComponentStyles.readyPreviewItem}>
        <Typography variant="caption" sx={managerComponentStyles.readyPreviewLabel}>
          {MANAGER_READY_COVENANT_PERIOD_LABEL}
        </Typography>
        <Typography variant="body2" sx={managerComponentStyles.readyPreviewValue}>
          {formatReadyDocumentPeriod(covenant.childPeriod, covenant.yearPeriod)}
        </Typography>
      </Box>
      <Box sx={managerComponentStyles.readyPreviewItem}>
        <Typography variant="caption" sx={managerComponentStyles.readyPreviewLabel}>
          {MANAGER_READY_COVENANT_DEADLINE_LABEL}
        </Typography>
        <Typography variant="body2" sx={managerComponentStyles.readyPreviewValue}>
          {covenant.deadline || MANAGER_READY_COVENANTS_EMPTY_VALUE}
        </Typography>
      </Box>
      <Box sx={managerComponentStyles.readyPreviewItem}>
        <Typography variant="caption" sx={managerComponentStyles.readyPreviewLabel}>
          {MANAGER_READY_COVENANT_STATUS_LABEL}
        </Typography>
        <Box sx={managerComponentStyles.readyInlineStatus}>
          <StatusChipWithTooltip
            label={covenantStatusConfig.label}
            color={covenantStatusConfig.color}
            description={covenantStatusConfig.description}
            size="small"
          />
        </Box>
      </Box>
      <Box sx={managerComponentStyles.readyPreviewItem}>
        <Typography variant="caption" sx={managerComponentStyles.readyPreviewLabel}>
          {MANAGER_READY_COVENANT_TARGET_STATUS_LABEL}
        </Typography>
        <Box sx={managerComponentStyles.readyInlineStatus}>
          <StatusChipWithTooltip
            label={covenantTargetStatusConfig.label}
            color={covenantTargetStatusConfig.color}
            description={covenantTargetStatusConfig.description}
            size="small"
            chipSx={targetStatusChipSx}
          />
        </Box>
      </Box>
    </Box>
  );
};


